import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import { toast } from 'sonner';

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [inv, setInv] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | valid | error | done
  const [errorMsg, setErrorMsg] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/invitations/${token}`)
      .then(r => { setInv(r.data); setStatus('valid'); })
      .catch(err => {
        setErrorMsg(err.response?.data?.detail || 'This invitation is invalid or has expired.');
        setStatus('error');
      });
  }, [token]);

  const handleAccept = async () => {
    if (!name.trim()) { toast.error('Please enter your name'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/invitations/${token}/accept`, { name, password });
      toast.success('Account created! Welcome to My Quality');
      login(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-xl border border-slate-200">
        {status === 'loading' && (
          <div className="text-center py-8">
            <Loader className="animate-spin mx-auto text-indigo-500 mb-4" size={32} />
            <p className="text-slate-600">Verifying invitation…</p>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center py-8">
            <AlertTriangle size={40} className="mx-auto text-rose-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invitation Invalid</h2>
            <p className="text-slate-600 mb-6">{errorMsg}</p>
            <Button variant="outline" onClick={() => navigate('/login')}>Back to Login</Button>
          </div>
        )}

        {status === 'done' && (
          <div className="text-center py-8">
            <CheckCircle size={40} className="mx-auto text-emerald-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">All set!</h2>
            <p className="text-slate-600">Redirecting you to your dashboard…</p>
          </div>
        )}

        {status === 'valid' && inv && (
          <>
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-black text-indigo-600">MQ</span>
              </div>
              <h2 className="text-2xl font-bold text-slate-900">You're invited!</h2>
              <p className="text-slate-600 mt-1">
                <strong>{inv.invited_by_email}</strong> has invited you to join{' '}
                <strong>{inv.company_name}</strong> as <strong>{inv.role}</strong>.
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 mb-6 text-sm text-slate-600">
              Account will be created for: <strong>{inv.email}</strong>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="inv-name">Your Full Name *</Label>
                <Input
                  id="inv-name"
                  placeholder="Jane Doe"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  data-testid="invite-name-input"
                />
              </div>
              <div>
                <Label htmlFor="inv-pass">Create Password *</Label>
                <Input
                  id="inv-pass"
                  type="password"
                  placeholder="Minimum 6 characters"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  data-testid="invite-password-input"
                />
              </div>
              <div>
                <Label htmlFor="inv-confirm">Confirm Password *</Label>
                <Input
                  id="inv-confirm"
                  type="password"
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  data-testid="invite-confirm-input"
                />
              </div>
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleAccept}
                disabled={submitting}
                data-testid="invite-accept-btn"
              >
                {submitting ? 'Creating account…' : 'Accept & Join'}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default InviteAccept;
